import { FEATURE_FLAGS } from '@app/app-constants'
import { appLink } from '@app/config/appLink'
import { ProtectedRoute } from '@app/Auth/ProtectedRoute'
import { FeatureFlagGate } from '@app/FeatureFlag/FeatureFlagGate'
import { AdaptationLibraryDrawer } from '@app/modules/AdaptationLibrary/components/AdaptationLibrary.drawer'
import { AdaptationLibraryPage } from '@app/modules/AdaptationLibrary/pages/AdaptationLibrary.page'
import RiskLayout from '@app/components/Risk/layout/RiskLayout'
import { RiskLandingPage } from '@app/components/Risk/Overview/RiskLandingPage'
import { PrioritisationLandingPage } from '@app/components/Risk/Prioritisation/PrioritisationLandingPage'
import { RiskPage } from '@app/routes/RiskPage/RiskPage'
import { StoryBuilderContent, StoryBuilderPanel } from '@app/components/Risk/Story/Builder'
import { RiskStoryDetail } from '@app/components/Risk/Story/RiskStoryDetail'
import RiskStoryList from '@app/components/Risk/Story/RiskStoryList'
import { WorkspaceEdit } from '@app/components/Risk/Workspaces/WorkspaceEdit'
import WorkspacesCreate from '@app/components/Risk/Workspaces/WorkspacesCreate'
import WorkspacesDialog from '@app/components/Risk/Workspaces/WorkspacesDialog'
import WorkspacesManager from '@app/components/Risk/Workspaces/WorkspacesManager'
import { PrioritisationPage } from '@app/routes/PrioritisationPage'
import { SummaryPage } from '@app/routes/SummaryPage'
import { Navigate, Outlet, Route } from 'react-router-dom'
import { UnauthorizedPage } from './UnauthorisedPage/UnauthorizedPage'
import RiskProfilePage from '@app/modules/Risk/Profile/pages/risk-profile.page'
import ProfileAnalysisPage from '@app/modules/Risk/Profile/pages/profile-analysis.page'
import SelectedElementAnalysisPage from '@app/modules/Risk/Profile/pages/selected-element.page'
import { SavedWorkspacesPage } from '@app/modules/Risk/Workspaces/pages/saved-workspaces.page'
import { CreateEditWorkspace } from '@app/modules/Risk/Workspaces/pages/create-edit-workspace.page'

export default function RiskRoutes() {
  return (
    <Route path="risk">
      <Route index element={<Navigate to="overview" replace />} />
      <Route
        path="overview"
        element={
          <ProtectedRoute>
            <RiskLayout>
              <RiskLandingPage />
            </RiskLayout>
          </ProtectedRoute>
        }
      />
      <Route path="profile">
        <Route
          index
          element={
            <FeatureFlagGate
              flag={FEATURE_FLAGS.RISK_PROFILE}
              fallback={<UnauthorizedPage feature={FEATURE_FLAGS.RISK_PROFILE} />}
            >
              <ProtectedRoute>
                <RiskLayout>
                  <RiskProfilePage />
                </RiskLayout>
              </ProtectedRoute>
            </FeatureFlagGate>
          }
        />
        <Route
          path="analysis"
          element={
            <FeatureFlagGate
              flag={FEATURE_FLAGS.RISK_PROFILE}
              fallback={<UnauthorizedPage feature={FEATURE_FLAGS.RISK_PROFILE} />}
            >
              <ProtectedRoute>
                <RiskLayout>
                  <ProfileAnalysisPage />
                </RiskLayout>
              </ProtectedRoute>
            </FeatureFlagGate>
          }
        />

        {/* Selected Element */}
        <Route
          path="element/:assetType/:aggregateId"
          element={
            <FeatureFlagGate
              flag={FEATURE_FLAGS.RISK_PROFILE}
              fallback={<UnauthorizedPage feature={FEATURE_FLAGS.RISK_PROFILE} />}
            >
              <ProtectedRoute>
                <RiskLayout>
                  <SelectedElementAnalysisPage />
                </RiskLayout>
              </ProtectedRoute>
            </FeatureFlagGate>
          }
        />
      </Route>
      // WORKSPACES V2
      <Route
        path="workspaces"
        element={
          <FeatureFlagGate
            flag={FEATURE_FLAGS.WORKSPACE_V2}
            fallback={<UnauthorizedPage feature={FEATURE_FLAGS.WORKSPACE_V2} />}
          >
            <ProtectedRoute>
              <SavedWorkspacesPage />
            </ProtectedRoute>
          </FeatureFlagGate>
        }
      />
      <Route
        path="workspaces/create"
        element={
          <FeatureFlagGate
            flag={FEATURE_FLAGS.WORKSPACE_V2}
            fallback={<UnauthorizedPage feature={FEATURE_FLAGS.WORKSPACE_V2} />}
          >
            <ProtectedRoute>
              <CreateEditWorkspace />
            </ProtectedRoute>
          </FeatureFlagGate>
        }
      />
      <Route
        path="workspaces/:workspaceId/edit"
        element={
          <FeatureFlagGate
            flag={FEATURE_FLAGS.WORKSPACE_V2}
            fallback={<UnauthorizedPage feature={FEATURE_FLAGS.WORKSPACE_V2} />}
          >
            <ProtectedRoute>
              <CreateEditWorkspace />
            </ProtectedRoute>
          </FeatureFlagGate>
        }
      />
      <Route
        path="explorer"
        element={
          <ProtectedRoute>
            <RiskLayout>
              <RiskPage />
            </RiskLayout>
          </ProtectedRoute>
        }
      >
        // Legacy workspaces. Leaving in tact for now.
        <Route path="workspaces" element={<WorkspacesDialog />}>
          <Route path="saved" element={<WorkspacesManager />} />
          <Route path="create" element={<WorkspacesCreate />} />
          <Route path=":workspaceId/edit" element={<WorkspaceEdit />} />
        </Route>
        <Route path="story-builder" element={<StoryBuilderPanel />}>
          <Route index element={<StoryBuilderContent />} />
          <Route path=":storyId" element={<StoryBuilderContent />} />
        </Route>
      </Route>
      <Route
        path="summary"
        element={
          <FeatureFlagGate
            flag={FEATURE_FLAGS.SUMMARY_PAGE}
            fallback={<Navigate to={appLink.homeWelcome.path} />}
          >
            <ProtectedRoute>
              <RiskLayout>
                <SummaryPage />
              </RiskLayout>
            </ProtectedRoute>
          </FeatureFlagGate>
        }
      />
      <Route path="prioritisation">
        <Route
          index
          element={
            <FeatureFlagGate
              flag={FEATURE_FLAGS.PRIORITISATION_PAGE}
              fallback={<Navigate to={appLink.homeWelcome.path} />}
            >
              <ProtectedRoute>
                <RiskLayout>
                  <PrioritisationLandingPage />
                </RiskLayout>
              </ProtectedRoute>
            </FeatureFlagGate>
          }
        />
        <Route
          path="start"
          element={
            <FeatureFlagGate
              flag={FEATURE_FLAGS.PRIORITISATION_PAGE}
              fallback={<Navigate to={appLink.homeWelcome.path} />}
            >
              <ProtectedRoute>
                <RiskLayout>
                  <PrioritisationPage />
                </RiskLayout>
              </ProtectedRoute>
            </FeatureFlagGate>
          }
        />
      </Route>
      <Route path="stories">
        <Route
          index
          element={
            <ProtectedRoute>
              <RiskLayout>
                <RiskStoryList />
              </RiskLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path=":storyId/:slug"
          element={
            <ProtectedRoute redirectTo="./view">
              <RiskLayout>
                <RiskStoryDetail />
              </RiskLayout>
            </ProtectedRoute>
          }
        />
      </Route>
      <Route
        path="adaptation-library"
        element={
          <FeatureFlagGate
            flag={FEATURE_FLAGS.ADAPTATION_LIBRARY}
            fallback={<Navigate to={appLink.homeWelcome.path} />}
          >
            <ProtectedRoute>
              <RiskLayout>
                <AdaptationLibraryPage />
                <Outlet />
              </RiskLayout>
            </ProtectedRoute>
          </FeatureFlagGate>
        }
      >
        <Route path=":adaptationId" element={<AdaptationLibraryDrawer />} />
      </Route>
    </Route>
  )
}
